import clsx from "clsx";
import dayjs from "dayjs";
import sample from "lodash/sample";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useState } from "react";
import { Seomatic } from "react-seomatic";

import { PLASMIC } from "@abnormal/plasmic-init";
import BlogLayoutNewsletterForm from "@components/BlogLayoutNewsletterForm";
import ColumnLayout from "@components/ColumnLayout";
import ContentSections from "@components/ContentSections";
import EntryCarousel from "@components/EntryCarousel";
import EntryThumbnail from "@components/EntryThumbnail";
import Heading from "@components/Heading";
import LegacyContent from "@components/LegacyContent";
import PageSections from "@components/PageSections";
import SidebarCallToAction from "@components/SidebarCallToAction";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { getEntryCategoryArray } from "@lib/getEntryCategoryArray";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import {
  ComponentRenderData,
  PlasmicComponent,
  PlasmicRootProvider,
  extractPlasmicQueryData
} from "@plasmicapp/loader-nextjs";
import QueryPost from "@queries/blog/QueryPost.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";
import Image from "next/image";

import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Divider
} from "@chakra-ui/react";
import Authors from "@components/Authors";
import { headerHeight } from "@lib/headerHeight";
import Script from "next/script";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, { section: "blog", limit: 20 });

	const paths = [];

	response.entries.map(async (post) => {
		console.log(`Generating /blog/${post.slug}`);

		paths.push({
			params: { slug: post.slug }
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
  const uri = `/blog/${slug}`;
  
  const plasmicData = await PLASMIC.maybeFetchComponentData([
    "BladesDynamicDemoBlade",
    "Footer"
  ], {
    // deferChunks: true,
  });
  if (!plasmicData) {
    throw new Error("No Plasmic design found");
  }

	// Fetch the page's data
	const response = await craft(
		QueryPost,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		post,
		posts,
		primaryNavigation,
		primaryCallToActions,
		persistentNav
  } = response;
  
  // Cache the necessary Plasmic data fetched for the page
  const queryCache = await extractPlasmicQueryData(
    <PlasmicRootProvider loader={PLASMIC} prefetchedData={plasmicData}>
      <PlasmicComponent
        component="BladesDynamicDemoBlade"
      />
    </PlasmicRootProvider>
  );

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions
			},
			pageEntry,
			persistentNav,
			post,
      posts,
      plasmicData,
      queryCache
		},
		!post,
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	post: any;
  posts: any;
  plasmicData: ComponentRenderData;
  queryCache: Record<string, any>;
}

export default function Page({ pageEntry, post, posts, plasmicData, queryCache }: IProps): JSX.Element {
	useSetHeaderTheme(pageEntry.headerTheme);

	// Select just one call to action at random

	const parsedDate = dayjs(post.postDate);
	let formData;
	const sidebarCallToAction = sample(pageEntry.callToActions);
	const relatedPosts = posts.slice(0, 4);

	const handleImageHeight = (newImageHeight) => {
		setThumbnailImageHeight((oldHeight) =>
			newImageHeight > 0 ? newImageHeight : oldHeight
		);
	};

	const [thumbnailImageHeight, setThumbnailImageHeight] = useState(100);

	const heroImage = !!post.heroImage ? post.heroImage[0] : null;
	const heroImageBlur = !!post.heroImageBlur ? post.heroImageBlur[0] : null;
	const mobileHeroImage = !!post.mobileHeroImage
		? post.mobileHeroImage[0]
		: null;
	const mobileHeroImageBlur = !!post.mobileHeroImageBlur
		? post.mobileHeroImageBlur[0]
		: null;

	let faqs = [];
	let faqSchema = {};

	if (post.faqSchema?.length) {
		post.faqSchema.map((item) => {
			faqs.push({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: {
					"@type": "Answer",
					text: item.answer
				}
			});
		});

		faqSchema = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faqs
		};
	}

	return (
    <PlasmicRootProvider
      loader={PLASMIC}
      prefetchedData={plasmicData}
      prefetchedQueryData={queryCache}
    >
			<Seomatic Head={Head} {...post.seomatic} />

			{post.faqSchema?.length > 0 && (
				<Script
					type="application/ld+json"
					id="faqSchema"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
				/>
			)}

			<Box className={clsx("relative", "pb-15")} pt={headerHeight + 40}>
				<ColumnLayout>
					<ColumnLayout.MainColumn>
						<div className={clsx("")}>
							<div className={clsx("EntryHeader__breadcrumb", "mb-7.5")}>
								<Breadcrumb>
									<BreadcrumbItem>
										<BreadcrumbLink href={`/${pageEntry.uri}`}>
											{pageEntry.title}
										</BreadcrumbLink>
									</BreadcrumbItem>

									<BreadcrumbItem hidden={!post.blogCategory.length}>
										<BreadcrumbLink href={`/${post.blogCategory[0]?.uri}`}>
											{post.blogCategory[0]?.title}
										</BreadcrumbLink>
									</BreadcrumbItem>

									<BreadcrumbItem isCurrentPage hidden>
										<BreadcrumbLink href={post.url}>
											{post.title}
										</BreadcrumbLink>
									</BreadcrumbItem>
								</Breadcrumb>
							</div>
							<Heading size="h1" mb={3}>
								{post.title}
							</Heading>
							<Box fontSize={20}>{post.summary}</Box>
							{!!post.postAuthors && (
								<Box className={clsx("EntryHeader__authors")} mb={3}>
									<Authors authors={post.postAuthors} />
								</Box>
							)}

							{!!parsedDate && (
								<div
									className={clsx(
										"EntryHeader__categoryTokens",
										"mb-5 text-xs"
									)}
								>
									{parsedDate.format("MMMM D, YYYY")}
								</div>
							)}
							<Divider mb={7} opacity={1} borderColor="black-01" />
						</div>
						{!!post.contentSections && (
							<ContentSections sections={post.contentSections} />
						)}
						{!!post.legacyBlogContent && (
							<LegacyContent content={post.legacyBlogContent} />
						)}
					</ColumnLayout.MainColumn>
          <ColumnLayout.SidebarColumn className={clsx("lg:pb-15")}>
            {!!post?.image[0]?.entryThumb && (
              <Image
                src={process.env.NODE_ENV === "production" ? post.image[0]?.entryThumb[0]?.url : post.image[0]?.url}
                height={post.image[0]?.entryThumb[0]?.height / 2}
                width={post.image[0]?.entryThumb[0]?.width / 2}
                alt={post.title}
                className={clsx("EntryThumbnail__image")}
                draggable={false}
                blurDataURL={post.image[0]?.entryThumb[1].url}
              />
            )}
						{!!sidebarCallToAction && (
							<SidebarCallToAction
								heading={sidebarCallToAction.heading}
								image={sidebarCallToAction.image[0]}
								imageBlur={sidebarCallToAction.imageBlur[0]}
								target={sidebarCallToAction.target}
							/>
						)}

						<BlogLayoutNewsletterForm
							formHeading={pageEntry.formHeading}
							formDescription={pageEntry.formDescription}
							form={pageEntry?.form?.[0]}
							formData={formData}
							successMessage={pageEntry.successMessage}
						/>
					</ColumnLayout.SidebarColumn>
				</ColumnLayout>
			</Box>

			{pageEntry.footerModule?.length > 0 && (
				<div className="my-7.5">
					<PageSections
						sections={pageEntry.footerModule[0].pageSections}
						isPageModule
					/>
				</div>
      )}
      
      <PlasmicComponent
        component="BladesDynamicDemoBlade"
      />

			<div
				className={clsx(
					"max-w-screen-xl mx-auto",
					"px-7.5 md:px-15 lg:px-15 mb-7.5 lg:mb-7.5 pt-7.5",
					"text-2xl lg:text-3xl"
				)}
			>
				<Heading size="h2">Related Posts</Heading>
			</div>

			<div className={clsx("pb-30")}>
				<EntryCarousel
					isFlush={false}
					thumbnailImageHeight={thumbnailImageHeight}
					//thumbnailAspectRatio={"square"}
				>
					{posts.map((entry) => {
						const image = !!entry.image ? entry.image[0] : null;
						const imageBlur = !!entry.imageBlur ? entry.imageBlur[0] : null;
						return (
							<EntryThumbnail
								key={entry.id}
								image={image}
								imageBlur={imageBlur}
								heading={entry.title}
								summary={entry.summary}
								target={{ element: entry, type: "entry" }}
								categories={getEntryCategoryArray(entry)}
								onImageHeight={handleImageHeight}
								aspectRatio={"square"}
							/>
						);
					})}
				</EntryCarousel>
      </div>
      
    </PlasmicRootProvider>
	);
}
